/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { Form, Field as FormField, getInput, isEdited, reset, useForm } from "@formisch/react"
import clsx from "clsx"
import { AppWindowIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router"
import * as v from "valibot"

import { getAppTypeIdFromParams } from "../../features/application/application-url-utils"
import { UpdateApplicationTypeSchema } from "../../features/application-type/application-type-schema"
import type { ApplicationType } from "../../features/application-type/application-type-types"
import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { OrganizationViewModel } from "../../features/organization/organization-viewmodel"
import { AppRoutes } from "../../routes/routes"
import { isFieldInvalid } from "../../utils/is-field-invalid"
import { valueOrDash } from "../../utils/value-or-dash"
import { useViewModel } from "../app/app-hooks"
import { AppIcon } from "../components/app-icon"
import { BusyButton } from "../components/busy-button"
import { IconPicker } from "../components/icon-picker"
import { Truncate } from "../components/truncate"
import { Button } from "../primitives/button"
import { Card, CardContent, CardFooter } from "../primitives/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../primitives/empty"
import { Field, FieldError } from "../primitives/field"
import { Input } from "../primitives/input"

import styles from "./application-type-detail-view.module.css"

/**
 * Application Detail page for the ID in the route.
 *
 * The route loads the record and the organizations, and shows its spinner on a
 * cold load. "Application not found" appears when no record is there after
 * that, such as for a wrong ID or a failed request.
 */
export const ApplicationTypeDetailView = observer(() => {
    const appTypeId = getAppTypeIdFromParams(useParams())

    const appTypeVM = useViewModel(ApplicationTypeViewModel)
    const appType = appTypeVM.appTypeFor(appTypeId)

    return (
        <div className="w-content">
            <h1 className="m-0 mb-6 text-3xl font-bold text-primary">Application Detail</h1>

            <div className="max-w-175">
                {appTypeId !== null && appType ? (
                    // The key remounts the form when the ID in the route changes, so
                    // edits don't carry over to another application.
                    <ApplicationTypeDetailForm key={appTypeId} appTypeId={appTypeId} appType={appType} />
                ) : (
                    renderEmptyAppTypeDetail()
                )}
            </div>
        </div>
    )
})

/**
 * Renders the empty state when no application is available for the route ID,
 * either because the link is invalid or the record failed to load. Load errors
 * are also reported through the logger's toast.
 *
 * Provides a plain link back to All Applications. Using a Button rendered as
 * a link would give it `role="button"` through Base UI, misrepresenting its
 * navigation behavior to assistive technology.
 */
function renderEmptyAppTypeDetail() {
    return (
        <Empty className="border bg-background">
            <EmptyHeader>
                <EmptyMedia>
                    <AppWindowIcon />
                </EmptyMedia>
                <EmptyTitle>Application not found</EmptyTitle>
                <EmptyDescription>
                    Check the link, or go back to <Link to={AppRoutes.AllApplicationTypes}>All Applications</Link>.
                </EmptyDescription>
            </EmptyHeader>
        </Empty>
    )
}

interface Props {
    /**
     * ID from the route. It's a prop of its own because the generated
     * `ApplicationType.id` is optional, and this one is always a number.
     */
    appTypeId: number
    /**
     * Stored record. The form follows its description until the user edits it.
     */
    appType: ApplicationType
}

/**
 * Shows an application and lets an admin change its description and icon.
 *
 * Save stays disabled until something has changed and the description passes
 * `UpdateApplicationTypeSchema`. Discard returns both fields to what is stored.
 */
const ApplicationTypeDetailForm = observer(({ appTypeId, appType }: Props) => {
    // Remount the picker on discard to clear its internal state; see `discard`.
    const [pickerKey, setPickerKey] = useState(0)

    const appTypeVM = useViewModel(ApplicationTypeViewModel)
    const orgVM = useViewModel(OrganizationViewModel)

    const orgName = orgVM.organizations.find((org) => org.id === appType.organizationId)?.name
    const storedDescription = appType.description ?? ""

    const form = useForm({
        schema: UpdateApplicationTypeSchema,
        initialInput: { description: storedDescription },
        validate: "blur"
    })

    const input = getInput(form)
    // The schema trims the description on save, so compare trimmed values. Spaces
    // alone aren't a change.
    const descriptionChanged = (input.description ?? "").trim() !== storedDescription.trim()
    const hasChanges = descriptionChanged || input.icon !== undefined

    /**
     * Saves the description, then the icon, each in its own request.
     *
     * Each edit clears only once its own request succeeds, so a partly failed
     * save keeps what didn't save. A failed description stops the save before
     * the icon. The service shows the error as a toast.
     */
    const save = async (output: v.InferOutput<typeof UpdateApplicationTypeSchema>) => {
        if (output.description !== storedDescription.trim()) {
            const result = await appTypeVM.updateAppType(appTypeId, output.description)
            if (!result.success) return
            reset(form, { path: ["description"], initialInput: output.description })
        }

        if (output.icon) {
            const result = await appTypeVM.updateAppTypeIcon(appTypeId, output.icon)
            if (result.success) reset(form, { path: ["icon"] })
        }
    }

    /**
     * Discards edits and restores the stored description, clearing any selected icon.
     *
     * Rejected files never enter the form state, so resetting the form alone
     * cannot clear the picker's rejection message or native file input. Remounting
     * the picker resets both.
     */
    const discard = () => {
        reset(form, { initialInput: { description: storedDescription } })
        setPickerKey((key) => key + 1)
    }

    // The route shows a cached record at once and refreshes it after the form
    // mounts. Follow the stored description until the user edits it, so the
    // form picks up the refreshed one.
    useEffect(() => {
        if (!isEdited(form, { path: ["description"] })) {
            reset(form, { path: ["description"], initialInput: storedDescription })
        }
    }, [form, storedDescription])

    return (
        <Form of={form} onSubmit={save}>
            <Card>
                <CardContent>
                    <dl className={styles.table}>
                        <dt className={styles.key}>ID:</dt>
                        <dd className={clsx("tabular-nums", styles.value)}>{appTypeId}</dd>

                        <dt className={styles.key}>
                            <label htmlFor="application-type-description">Description:</label>
                        </dt>
                        <dd className={styles.value}>
                            <FormField of={form} path={["description"]}>
                                {(field) => {
                                    const invalid = isFieldInvalid(field)

                                    return (
                                        <Field data-invalid={invalid}>
                                            <Input
                                                {...field.props}
                                                id="application-type-description"
                                                type="text"
                                                value={field.input}
                                                aria-invalid={invalid}
                                                autoComplete="off"
                                                // Read-only rather than disabled. Enter here submits the
                                                // form, and a disabled field would drop the focus.
                                                readOnly={form.isSubmitting}
                                            />
                                            {invalid && <FieldError>{field.errors?.[0]}</FieldError>}
                                        </Field>
                                    )
                                }}
                            </FormField>
                        </dd>

                        <dt className={styles.key}>Package Name:</dt>
                        <dd className={styles.value}>
                            <Truncate kind="middle" text={valueOrDash(appType.sourcePackageName)} />
                        </dd>

                        <dt className={styles.key}>Platform:</dt>
                        <dd className={styles.value}>{valueOrDash(appType.platform)}</dd>

                        <dt className={styles.key}>Category:</dt>
                        <dd className={styles.value}>{valueOrDash(appType.category)}</dd>

                        <dt className={styles.key}>Organization:</dt>
                        <dd className={styles.value}>{valueOrDash(orgName)}</dd>

                        <dt className={styles.key}>
                            <label htmlFor="application-type-icon">Icon:</label>
                        </dt>
                        <dd className={styles.value}>
                            <FormField of={form} path={["icon"]}>
                                {(field) => (
                                    <IconPicker
                                        key={pickerKey}
                                        id="application-type-icon"
                                        value={field.input}
                                        onChange={field.onChange}
                                        disabled={form.isSubmitting}
                                        current={<AppIcon appTypeId={appTypeId} alt="Current icon" />}
                                    />
                                )}
                            </FormField>
                        </dd>
                    </dl>
                </CardContent>
                <CardFooter className="justify-end gap-2 border-t">
                    <Button
                        type="button"
                        variant="secondary"
                        size="action"
                        disabled={!hasChanges || form.isSubmitting}
                        onClick={discard}
                    >
                        Discard
                    </Button>
                    <BusyButton
                        type="submit"
                        size="action"
                        busy={form.isSubmitting}
                        disabled={!hasChanges || !v.is(UpdateApplicationTypeSchema, input)}
                    >
                        Save
                    </BusyButton>
                </CardFooter>
            </Card>
        </Form>
    )
})
